import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import * as types from '../../../constants/actionTypes';
import Notification from '../../Common/Notification';
import Button from '../../Common/Button';
import Icon from '../../Common/Icon';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import LocationContactAddDrawer from './LocationContactAddDrawer';
import './LocationContacts.scss';
import LocationContactImporter from './LocationContactImporter/LocationContactImporter';

export class LocationContacts extends React.Component {
    constructor(props, context) {
        super(props, context);

        this.state = {
            searchParams: {
                pageSize: 20,
                pageNumber: 1,
                locationKey: this.props.locationKey,
                sortBy: "firstName"
            },
            isLoadingContacts: true,
            showMore: true,
            showAddContactDrawer: false,
            showImportContactDrawer: false,
            contacts: []
        }
    }

    componentDidMount = async () => {
        this.loadContacts();
    }

    componentDidUpdate(prevProps) {
        // reload when the user changes locations
        // they could be searching for a location so make sure there is a value
        if (this.props.locationKey && this.props.locationKey !== prevProps.locationKey) {
            const newParams = {
                locationKey: this.props.locationKey
            }
            this.setState({
                contacts: [],
                searchParams: {
                    ...this.state.searchParams, ...newParams
                }
            }, this.loadContacts);
        }
    }

    loadContacts = async () => {
        try {
            this.setState({ isLoadingContacts: true });
            const response = await this.props.locationContactActions.search(this.state.searchParams);
            if (response.type === types.SEARCH_LOCATION_CONTACTS_SUCCESS) {
                // if at any point there are fewer than [searchParams.pageSize] results returned then we know we can't show more
                const showMore = (response.payload.results.length < this.state.searchParams.pageSize) ? false : true;
                const combinedContacts = this.state.contacts.concat(response.payload.results);
                this.setState({
                    contacts: combinedContacts,
                    showMore: showMore
                })
            } else if (response.type === types.SEARCH_LOCATION_CONTACTS_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            this.setState({ isLoadingContacts: false });
        }
    }

    onAddContact = () => {
        this.setState({ showAddContactDrawer: true });
    }

    onImportContact = () => {
        this.setState({ showImportContactDrawer: true });
    }

    addImportContactCancel = () => {
        this.setState({ showImportContactDrawer: false });
    }

    addContactCancel = () => {
        this.setState({ showAddContactDrawer: false });
    }

    addContactComplete = () => {
        this.setState({ showAddContactDrawer: false, contacts: [] }, this.loadContacts);
    }

    onRemoveContact = async (contact) => {
        try {

            const response = await this.props.locationContactActions.remove(this.props.locationKey, contact.userId);
            if (response.type === types.REMOVE_LOCATION_CONTACT_SUCCESS) {
                Notification("success", "", "Contact has been removed");

                // just remove the contact from the array instead of refreshing the list
                let updatedContacts = this.state.contacts.slice();
                const i = updatedContacts.findIndex(m => m.userId === contact.userId);
                updatedContacts.splice(i, 1);
                this.setState({ contacts: updatedContacts });

            } else if (response.type === types.REMOVE_LOCATION_CONTACT_FAILURE) {
                Notification("error", "Error", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        }
    }

    onEmailContact = (contact) => {
        const mailto = "mailto:" + contact.emailAddress;
        window.open(mailto, "_self");
    }

    onShowMore = () => {
        const newParams = {
            pageNumber: this.state.searchParams.pageNumber + 1
        }
        this.setState({
            isLoadingContacts: true,
            searchParams: {
                ...this.state.searchParams, ...newParams
            }
        },
            this.loadContacts
        );
    }

    render() {
        return (
            <div className="location-contacts">
                <div className="row align-items-center justify-content-between">
                    <div className="col">
                        <div className="dashboard-widget-title">Contacts</div>
                    </div>
                    <div className="col-auto">
                        <Button
                            type="button"
                            text="Import Contacts"
                            bootstrapClass="btn btn-primary me-2"
                            onClick={this.onImportContact}
                        />
                        <Button
                            type="button"
                            text="Add New Contact"
                            bootstrapClass="btn btn-primary"
                            onClick={this.onAddContact}
                        />
                    </div>
                </div>

                <div className="card mt-2">
                    <div className="card-body">
                        <LoadingIndicator isLoading={this.state.isLoadingContacts}>
                            <table className="table table-striped table-bordered table-hover">
                                <tbody>
                                    {
                                        this.state.contacts && this.state.contacts.length > 0 &&
                                        this.state.contacts.map(contact => {

                                            return (
                                                <tr key={contact.userId}>
                                                    <td><div className="nameLink" onClick={() => this.onEmailContact(contact)}>{contact.firstName} {contact.lastName}</div></td>
                                                    <td>{contact.jobTitle}</td>
                                                    <td>{contact.phoneNumber}</td>
                                                    <td className="remove"><span onClick={() => this.onRemoveContact(contact)}><Icon name="trash-alt" /></span></td>
                                                </tr>
                                            );
                                        })
                                    }
                                </tbody>
                            </table>

                            {this.state.showMore &&
                                <Button type="button" text="Show More" bootstrapClass="link float-end" onClick={this.onShowMore} />
                            }
                        </LoadingIndicator>
                    </div>
                </div>
                {this.state.showAddContactDrawer &&
                    <LocationContactAddDrawer
                        locationKey={this.props.locationKey}
                        onComplete={this.addContactComplete}
                        onCancel={this.addContactCancel}
                        locationTaskActions={this.props.locationTaskActions}
                        locationContactActions={this.props.locationContactActions}
                    />
                }
                {this.state.showImportContactDrawer &&
                    <LocationContactImporter
                        locationKey={this.props.locationKey}
                        onComplete={this.addContactComplete}
                        onCancel={this.addImportContactCancel}
                        locationActions={this.props.locationActions}
                        locationContactActions={this.props.locationContactActions}
                    />
                }
            </div>
        )
    }
}

LocationContacts.propTypes = {
    locationKey: PropTypes.string, // not required - user could be searching for a location
    locationContactActions: PropTypes.object.isRequired,
    locationTaskActions: PropTypes.object.isRequired,
    locationActions: PropTypes.object.isRequired
};

function mapStateToProps(state) {
    return {
        user: state.user,
    };
}

export default connect(mapStateToProps, null)(LocationContacts);
